import * as Yup from "yup";
import PageRequest from "../PageRequest";
import PageResponse from "../PageResponse";
import ClientFilter from "../clients/ClientFilter";
import qs from "qs";
import { format } from "date-fns";
import EntityId from "../EntityId";
import httpClient from "../config/HttpClient";
import { translations } from "../../../translations/common";

export const REMINDER_URL = "/reminders";
export const CLIENT_REMINDERS_URL = "/clients/reminders";

export interface ReminderFilter {
  dateFrom: string;
  dateTo: string;
  tableFilter: string;
  companyFilter: string;
  clientState: string;
}

export const parseFilter = (search: string): ReminderFilter => {
  const params = new URLSearchParams(search);
  return {
    dateFrom: params.get('dateFrom') || '',
    dateTo: params.get('dateTo') || '',
    tableFilter: params.get('tableFilter') || '',
    companyFilter: params.get('companyFilter') || '',
    clientState: params.get('clientState') || '',
  };
};

export interface ReminderData {
  date: string;
  comment: string;
}

export interface ReminderView extends ReminderData {
  clientId: string;
  companyTitle: string;
  clientState: string;
}

export const defaultReminderData: Readonly<ReminderData> = {
  date: new Date().toISOString().slice(0, 16),
  comment: "",
};

export const reminderSchema = Yup.object().shape({
  date: Yup.date().required(translations.reminders.form.validation.dateRequired),
  comment: Yup.string(),
});

export class ReminderModel {
  private static serializeParams(params: any) {
    return qs.stringify(params, {
      serializeDate: (date: Date) => format(date, "yyyy-MM-dd"),
    });
  }

  static async reminder(clientId: string): Promise<ReminderView> {
    return this.findOne(clientId);
  }

  static async findOne(clientId: string): Promise<ReminderView> {
    const response = await httpClient.get<ReminderView>(`/clients/${clientId}/reminders`);
    return response.data;
  }

  static async updateOrCreate(clientId: string, model: ReminderData): Promise<EntityId> {
    const response = await httpClient.put<EntityId>(`/clients/${clientId}/reminders`, model);
    return response.data;
  }

  static async delete(clientId: string): Promise<void> {
    await httpClient.delete(`/clients/${clientId}/reminders`);
  }

  static async listAll(page: PageRequest, filter: ReminderFilter): Promise<PageResponse<ReminderView>> {
    const response = await httpClient.get<PageResponse<ReminderView>>(REMINDER_URL, {
      params: { ...filter, ...page },
      paramsSerializer: this.serializeParams,
    });
    return response.data;
  }

  static async createBulk(reminder: ReminderData, filter: ClientFilter): Promise<void> {
    await httpClient.post(CLIENT_REMINDERS_URL, reminder, {
      params: filter,
      paramsSerializer: this.serializeParams,
    });
  }

  static async updateBulk(reminder: ReminderData, filter: ReminderFilter): Promise<void> {
    await httpClient.put(CLIENT_REMINDERS_URL, reminder, {
      params: filter,
      paramsSerializer: this.serializeParams,
    });
  }

  static async updateClientStates(clientState: string, filter: ReminderFilter): Promise<number> {
    const response = await httpClient.put<number>(
      `${CLIENT_REMINDERS_URL}/update-clients`,
      { clientState },
      {
        params: filter,
        paramsSerializer: this.serializeParams,
      }
    );
    return response.data;
  }

  static defaultValue(): ReminderData {
    return { ...defaultReminderData };
  }
}

export default ReminderModel;
